/**
 * Damage cost payment classification — single source of truth.
 *
 * Only `company_pay` is absorbed by the company. `user_pay`, `salary_deduction`
 * and `installment` are ALL paid by the employee, just through different
 * mechanisms. Code that tested `method === 'user_pay'` and treated everything
 * else as company-paid mislabelled salary deductions and installment plans as
 * "Covered by company".
 */
export const DAMAGE_PAYMENT_METHODS = [
  { value: 'user_pay',         label: 'User Pay (one-off)',      payer: 'employee', short: 'one-off payment' },
  { value: 'salary_deduction', label: 'Salary Deduction',        payer: 'employee', short: 'salary deduction' },
  { value: 'installment',      label: 'Installment Plan',        payer: 'employee', short: 'installment plan' },
  { value: 'company_pay',      label: 'Company Pays (waived)',   payer: 'company',  short: 'waived' },
];

const BY_VALUE = Object.fromEntries(DAMAGE_PAYMENT_METHODS.map(m => [m.value, m]));

/** True when the EMPLOYEE bears the cost (one-off, salary deduction or installment). */
export function isEmployeePaid(method) {
  return BY_VALUE[method]?.payer === 'employee';
}

/** True only for company_pay (cost waived / absorbed by the company). */
export function isCompanyPaid(method) {
  return BY_VALUE[method]?.payer === 'company';
}

/** Human label naming both the payer and the mechanism, e.g. "Charged to employee (salary deduction)". */
export function damagePayerLabel(method) {
  const m = BY_VALUE[method];
  if (!m) return null;
  return m.payer === 'company' ? 'Covered by company' : `Charged to employee (${m.short})`;
}

/** Colour for the payer label — amber when the employee pays, green when the company absorbs it. */
export function damagePayerColor(method) {
  return isEmployeePaid(method) ? '#fbbf24' : '#34d399';
}
